import React, { useState } from 'react';
import { arrayOf, func, instanceOf, objectOf, shape, string } from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Fuse from 'fuse.js';

import staticText from '../../localization/Application/webhooks';
import SignalCheckbox from '../SignalSelection/components/SignalCheckbox';
import { SearchChecklist } from '../SignalSelection/styles';
import InputField from '../InputField';

const SignalSearch = ({
  signals,
  selectedSignals,
  setSearchActive,
  handleSignalClick,
}) => {
  const { label, emptyState } = staticText.addModal.stepperSections[1].search;

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const fuse = new Fuse(Object.values(signals).flat(), {
    keys: ['groupName', 'name', 'description'],
    includeScore: true,
    // A threshold of 0.0 requires a perfect match;
    // a threshold of 1.0 would match anything
    threshold: 0.3,
  });

  const clearSearch = () => {
    setSearchInput('');
    setSearchResults([]);
    setSearchActive(false);
  };

  const handleInputChange = (event) => {
    if (event.target.value === '') {
      clearSearch();
      return;
    }
    setSearchActive(true);
    setSearchInput(event.target.value);
    // fuzzy search all signals
    const results = fuse.search(event.target.value);
    const signalItems = results.map(result => result.item);
    setSearchResults([...signalItems]);
  };

  return (
    <React.Fragment>
      <Box sx={{ mb: 3 }}>
        <InputField
          id="signal-search"
          data-testid="signal-search"
          inputName={label}
          inputValues={searchInput}
          handleChange={handleInputChange}
          inputPlaceholder={label}
          fullWidth
          startAdornment={
            <SearchRoundedIcon fontSize="small" />
          }
          endAdornment={
            <IconButton
              aria-label="clear search"
              onClick={clearSearch}
              edge="end"
              sx={{ border: 0 }}
            >
              <CloseRoundedIcon name="clear search" fontSize="small" />
            </IconButton>
          }
        />
      </Box>

      {searchInput && (
        <Box display="flex" flexDirection="column" flexGrow={1} overflow="hidden">
          {searchResults.length > 0 ? (
            <SearchChecklist>
              {searchResults.map(({
                name, groupName, description,
              }) => {
                const signalKey = `${groupName}.${name}`;
                return (
                  <li key={signalKey}>
                    <SignalCheckbox
                      displayName={signalKey}
                      signalKey={signalKey}
                      description={description}
                      selectedSignals={selectedSignals}
                      handleSignalClick={handleSignalClick}
                    />
                  </li>
                );
              })}
            </SearchChecklist>
          ) : (
            <Box display="flex" flexGrow={1} justifyContent="center" alignItems="center">
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box height="120px" sx={{ img: { width: '100%', height: '100%' } }}>
                  <img src="/assets/images/garage.png" alt="" />
                </Box>
                <Typography variant="bold" marginTop={3} marginBottom={1}>{emptyState.title}</Typography>
                <Typography variant="body2">{emptyState.description}</Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </React.Fragment>
  );
};

export default SignalSearch;

SignalSearch.propTypes = {
  signals: objectOf({
    [string]: arrayOf(shape({
      name: string,
      groupName: string,
      description: string,
    })),
  }).isRequired,
  selectedSignals: instanceOf(Set).isRequired,
  setSearchActive: func.isRequired,
  handleSignalClick: func.isRequired,
};
